import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';

import { AccessDeniedState, DashboardLoading } from '../../components/LoadingStates';
import selectUser from '../../store/selectors/appSelector';
import { ROUTE_CONSTANTS } from '../app/RouteBuilding/routeConstants';

import AdminAgencySpecificDashboard from './AdminAgencySpecificDashboard/AdminAgencySpecificDashboard';
import GlobalDashboard from './GlobalDashboard/GlobalDashboard';
import RecruiterAgencyDashboard from './RecruiterAgencyDashboard/RecruiterAgencyDashboard';
import { useDashboardPermissions } from './hooks';

/**
 * Parent Dashboard component that handles routing and permissions
 * - Checks user role and permissions
 * - Routes to appropriate child component
 * - Handles unauthorized access
 */
const Dashboard = () => {
  const user = useSelector(selectUser);
  const location = useLocation();
  const navigate = useNavigate();

  const { isAdmin, isRecruiter, isAdminOrRecruiter, pathSelectedAgencyId, canViewAgency } =
    useDashboardPermissions(user);

  // Handle unauthorized access
  if (pathSelectedAgencyId && isRecruiter && !canViewAgency) {
    const dashboardPath = `/${ROUTE_CONSTANTS.DASHBOARD}`;
    navigate(`${dashboardPath}${location?.search}`);
    return <DashboardLoading />;
  }

  // No access for non-admin/non-recruiter users
  if (!isAdminOrRecruiter) {
    return <AccessDeniedState />;
  }

  // Route to appropriate dashboard based on user role and context
  if (isRecruiter) {
    // Recruiters always see their own agency dashboard
    return <RecruiterAgencyDashboard />;
  }

  if (isAdmin) {
    if (pathSelectedAgencyId) {
      // Admin viewing a specific agency
      return <AdminAgencySpecificDashboard />;
    }
    // Admin viewing global dashboard
    return (
      <Box data-testid="admin-dashboard">
        <GlobalDashboard />
      </Box>
    );
  }

  // Fallback loading state
  return <DashboardLoading />;
};

export default Dashboard;
